import type { FlotiqStore } from "@jti/fe-common";
import type { Translate } from "@jti/ui/providers/useTranslations";

import type {
  StoreFiltersCategoriesEnabledParams,
  StoreLocatorFilter,
  StoreLocatorFilterCategory,
  StoreLocatorTranslationsFilter,
} from "../types";

const CATEGORIES = ["product_types", "services"] as (keyof Pick<FlotiqStore, "product_types" | "services">)[];
const NEW_CATEGORIES = [
  "product_types",
  "product_types_vending_machine",
  "services",
  "product_attributes",
  "storetype",
  "product_attributes_vending_machine",
] as (keyof Pick<
  FlotiqStore,
  | "product_types"
  | "services"
  | "product_attributes"
  | "storetype"
  | "product_types_vending_machine"
  | "product_attributes_vending_machine"
>)[];

const applyConfigFilters = (filters: StoreLocatorFilter = [], filtersStatus?: StoreFiltersCategoriesEnabledParams) => {
  if (!filtersStatus) return filters;

  return filters.filter((filter) => {
    if (filter.key === "product_types") return filtersStatus.isProductTypesFilterEnabled;
    if (filter.key === "services") return filtersStatus.isStoreServicesFilterEnabled;
    if (filter.key === "ProductFlavours") return filtersStatus.isProductFlavoursFilterEnabled;
    if (filter.key === "ProductColors") return filtersStatus.isProductColorsFilterEnabled;
    if (filter.key === "storetype") return filtersStatus.isStoreTypeFilterEnabled;
    return true;
  });
};

const translateFilters = (key: string, filtersTranslations: StoreLocatorTranslationsFilter[]) => {
  return filtersTranslations?.find(({ filterKey }) => filterKey === key)?.translatedFilterKey || key;
};

const isProductFlavourLYO = (key: string) => key === "ProductFlavoursLYO" || key === "ProductFlavoursLYOVendingMachine";

const isProductFlavourItem = (key: string) => isProductFlavourLYO(key) || key === "ProductFlavoursVendingMachine";

const addFilterToCategory = (
  filter: StoreLocatorFilterCategory,
  itemValue: string,
  isLYO: boolean,
  filtersTranslations: StoreLocatorTranslationsFilter[]
) => {
  if (!filter.filters.some(({ key }) => key === itemValue)) {
    const name = translateFilters(itemValue, filtersTranslations);

    filter.filters.push({
      name,
      key: itemValue,
      value: itemValue,
      label: name,
      ...(isLYO && { isLYO }),
    });
  }
};

export const getFiltersBaseOnFlotiqResponse = (
  __: Translate,
  stores: FlotiqStore[],
  filtersTranslations: StoreLocatorTranslationsFilter[]
) => {
  const filters = stores.reduce(
    (acc: StoreLocatorFilter, store: FlotiqStore) => {
      CATEGORIES.forEach((category) => {
        store[category]?.forEach((item) => {
          const filter = acc.find((filter) => filter.key === category);
          if (!filter?.filters.find((filter) => filter.key === item.key)) {
            const name = translateFilters(item.key, filtersTranslations);
            filter?.filters.push({
              name: name,
              key: item.key,
              value: item.key,
              label: name,
            });
          }
        });
      });
      return acc;
    },
    [
      {
        name: __("storelocator.filters.category.product_types"),
        key: "product_types",
        filters: [],
      },
      {
        name: __("storelocator.filters.category.services"),
        key: "services",
        filters: [],
      },
    ]
  );

  return filters;
};

export const getNewFiltersBaseOnFlotiqResponse = (
  __: Translate,
  stores: FlotiqStore[],
  filtersTranslations: StoreLocatorTranslationsFilter[],
  enabledFiltersCategories?: StoreFiltersCategoriesEnabledParams
) => {
  const initialFiltersObj: StoreLocatorFilter = [
    {
      name: __("storelocator.filters.category.product_types"),
      key: "product_types",
      alternativeKeys: ["product_types_vending_machine"],
      filters: [],
    },
    {
      name: __("storelocator.filters.category.product_flavours"),
      key: "ProductFlavours",
      filters: [],
    },
    {
      name: __("storelocator.filters.category.product_colors"),
      key: "ProductColors",
      filters: [],
    },
    {
      name: __("storelocator.filters.category.store_type"),
      key: "storetype",
      filters: [],
    },
    {
      name: __("storelocator.filters.category.services"),
      key: "services",
      filters: [],
    },
  ];

  const filters = stores.reduce((acc: StoreLocatorFilter, store: FlotiqStore) => {
    NEW_CATEGORIES.forEach((category) => {
      if (category === "product_attributes" || category === "product_attributes_vending_machine") {
        store[category]?.forEach((productAttributes) => {
          productAttributes?.values.forEach((item) => {
            const itemValue = item.value as string;
            const isLYO = isProductFlavourLYO(productAttributes.key);
            let filter = acc.find(({ key }) => key === productAttributes.key);

            if (!filter && isProductFlavourItem(productAttributes.key)) {
              filter = acc.find(({ key }) => key === "ProductFlavours");
            }

            if (filter) {
              addFilterToCategory(filter, itemValue, isLYO, filtersTranslations);
            }
          });
        });
      } else if (category === "storetype") {
        const storeType = store.storetype;
        const filter = acc.find((filter) => filter.key === category);
        if (!!storeType && filter && !filter?.filters.some(({ key }) => key === storeType)) {
          const name = translateFilters(storeType, filtersTranslations);
          filter.filters.push({
            name: name,
            key: storeType,
            value: storeType,
            label: name,
          });
        }
      } else
        store[category]?.forEach((item) => {
          const filter = acc.find((filter) => filter.key === category || filter.alternativeKeys?.includes(category));
          if (filter && !filter?.filters.some(({ key }) => key === item.key) && item.key !== "") {
            const name = translateFilters(item.key, filtersTranslations);
            filter.filters.push({
              name: name,
              key: item.key,
              value: item.key,
              label: name,
            });
          }
        });
    });
    return acc;
  }, initialFiltersObj);

  return applyConfigFilters(filters, enabledFiltersCategories);
};
